import { useState, useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';

import { getViewableFileUrl } from '../../../services/attachmentService';

import type { CarouselMedia, CarouselSource } from './carouselMedia';

/** Maps item id → resolved data URL (image/video/pdf ready to render) */
type FileCache = Map<string, string>;

const MIME_TO_EXT: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/mp4': 'm4a',
  'audio/wav': 'wav',
  'audio/ogg': 'ogg',
  'audio/aac': 'aac',
};

interface UseCarouselLazyLoadResult {
  getFileUrl: (id: string) => string | undefined;
  isLoading: (id: string) => boolean;
  hasError: (id: string) => boolean;
}

/**
 * Manages on-demand fetching of full item data for the carousel.
 * Only fetches the current item and its immediate neighbours (±1).
 * Caches all fetched files for the lifetime of the modal.
 *
 * @remarks
 * **Where the bytes come from** is the `source` (GitLab #1539): request
 * attachments by default, a conversation's message files for the
 * correspondence panel. The hook owns WHEN to load (current ±1), the
 * per-modal cache and the native temp-file dance below; the source owns HOW.
 * A source resolving `null` leaves the item untouched: not loaded, not an
 * error. An item with `previewable: false` is never asked for at all.
 *
 * **Offline Behavior**: When offline, the source's load will fail and
 * `hasError` will return true for affected items. The carousel will
 * display error UI with the item name. Thumbnails (if present on the item)
 * may still display during loading.
 *
 * **Abort**: the signal handed to the source is aborted when the window
 * moves or the hook unmounts, so a source that can cancel may. A result that
 * lands after the abort is still kept, keyed by id (see `fetchItem`); only an
 * unmount skips the re-render.
 *
 * @param items - Array of carousel items to manage loading for
 * @param currentIndex - Currently visible item index in the carousel
 * @param source - How to get an item's bytes
 * @returns Object with getFileUrl, isLoading, and hasError functions
 */
export const useCarouselLazyLoad = (
  items: CarouselMedia[],
  currentIndex: number,
  source: CarouselSource,
): UseCarouselLazyLoadResult => {
  const cacheRef = useRef<FileCache>(new Map());
  const loadingRef = useRef<Set<string>>(new Set());
  const errorRef = useRef<Set<string>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);
  /**
   * The source, read at call time, so `fetchItem` is stable and a rebuilt
   * source object (the correspondence panel's, on an `online` flip) never
   * means a load in flight is asked of a stale closure.
   */
  const sourceRef = useRef(source);
  sourceRef.current = source;
  /** Only an unmount may skip the re-render; an abort is not an unmount. */
  const mountedRef = useRef(true);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchItem = useCallback(
    async (item: CarouselMedia, onScreenId: string, signal: AbortSignal) => {
      const id = item.id;
      if (
        item.previewable === false ||
        cacheRef.current.has(id) ||
        loadingRef.current.has(id) ||
        errorRef.current.has(id)
      ) {
        return;
      }

      loadingRef.current.add(id);
      forceUpdate(n => n + 1);

      try {
        // Check if aborted before making the request
        if (signal.aborted) {
          loadingRef.current.delete(id);
          return;
        }

        const result = await sourceRef.current.load(item, {
          signal,
          onScreenId,
        });

        if (result === null) {
          // Not now: nothing recorded, so the next pass asks again.
          return;
        }

        // ⚠️ AN ABORTED LOAD THAT LANDS IS STILL KEPT (review, 2026-10-06).
        // The abort fires on every index change, so a swipe onto a neighbour
        // mid-flight aborted the very load that neighbour was waiting on; the
        // bytes were dropped, the id was neither cached nor errored, and the
        // page sat on a spinner until the index moved again. The cache is
        // keyed by id, so nothing stale can overwrite anything: keep them.
        const { base64, mimeType } = result;
        // react-native-video (and nitro-sound) on Android/iOS do not support data: URIs.
        // Write audio/video to a temp cache file and pass a file:// URI instead.
        if (
          Platform.OS !== 'web' &&
          (mimeType.startsWith('video/') || mimeType.startsWith('audio/'))
        ) {
          try {
            const RNFS = require('react-native-fs');
            const ext =
              MIME_TO_EXT[mimeType] ?? mimeType.split('/')[1] ?? 'bin';
            const path = `${
              RNFS.CachesDirectoryPath
            }/carousel_${encodeURIComponent(id)}.${ext}`;
            await RNFS.writeFile(path, base64, 'base64');
            cacheRef.current.set(id, `file://${path}`);
          } catch {
            // Fall back to data URL if RNFS fails for any reason
            cacheRef.current.set(id, getViewableFileUrl(base64, mimeType));
          }
        } else {
          cacheRef.current.set(id, getViewableFileUrl(base64, mimeType));
        }
      } catch {
        // Don't add to error set if aborted - it's not a real error
        if (!signal.aborted) {
          errorRef.current.add(id);
        }
      } finally {
        loadingRef.current.delete(id);
        if (mountedRef.current) {
          forceUpdate(n => n + 1);
        }
      }
    },
    [],
  );

  // Clean up RNFS temp files on unmount
  useEffect(() => {
    const cache = cacheRef.current;
    return () => {
      if (Platform.OS === 'web') return;
      try {
        const RNFS = require('react-native-fs');
        cache.forEach(url => {
          if (url.startsWith('file://')) {
            RNFS.unlink(url.replace('file://', '')).catch(() => {});
          }
        });
      } catch {
        /* RNFS not available */
      }
    };
  }, []);

  // Fetch current item and neighbors with abort support
  useEffect(() => {
    // Abort any in-flight requests from previous index
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this batch of requests
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const onScreenId = items[currentIndex]?.id ?? '';
    const indices = [currentIndex - 1, currentIndex, currentIndex + 1].filter(
      i => i >= 0 && i < items.length,
    );
    // A failed item is retried when it re-enters the window (review,
    // 2026-10-06): the error set used to hold an id for the modal's life.
    indices.forEach(i => errorRef.current.delete(items[i].id));
    indices.forEach(i => fetchItem(items[i], onScreenId, controller.signal));

    // Cleanup: abort on unmount or when dependencies change
    return () => {
      controller.abort();
    };
    // `source` is a dependency ON PURPOSE, read through `sourceRef`: a caller
    // that rebuilds its source (the correspondence panel does on a reconnect)
    // gets a fresh pass, which is what re-asks an item that answered "not
    // now". It costs nothing a load in flight: those are skipped by
    // `loadingRef`, and a result that lands after the abort is still kept.
  }, [currentIndex, items, fetchItem, source]);

  return {
    getFileUrl: (id: string) => cacheRef.current.get(id),
    isLoading: (id: string) => loadingRef.current.has(id),
    hasError: (id: string) => errorRef.current.has(id),
  };
};
